// Utilidades para manejo de fechas
// IMPORTANTE: Todas las fechas se manejan en YYYY-MM-DD local, NUNCA con timezone del servidor

/**
 * Parsea una fecha ISO string (YYYY-MM-DD) como una fecha local sin conversión UTC
 * Esto evita problemas de timezone donde "2024-10-30" se interpreta como UTC
 */
export function parseLocalDateString(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Formatea una fecha local a YYYY-MM-DD sin convertir a UTC
 * Esto es lo opuesto a parseLocalDateString
 * Ejemplo: new Date(2024, 9, 30) → "2024-10-30"
 */
export function formatLocalDateToString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function dateToString(date: Date | string | undefined | null): string {
  if (!date) return '';
  if (typeof date === 'string') {
    return date;
  }
  return date.toISOString().split('T')[0];
}

export function stringToDate(dateStr: string): Date {
  // Parsea como fecha local, no UTC
  return parseLocalDateString(dateStr);
}

export function formatDateForInput(date: Date | string | undefined | null): string {
  if (!date) return '';
  return dateToString(date);
}

/**
 * Formatea una fecha para mostrar en la UI
 * Si es string ISO, parsea como local primero
 * Formato: dd/mm/yyyy
 */
export function formatDateDisplay(date: Date | string | undefined | null): string {
  if (!date) return '';

  try {
    let dateObj: Date;

    if (typeof date === 'string') {
      // Si es ISO con hora (2025-12-02T18:43:28), parsear directamente
      if (date.includes('T')) {
        dateObj = new Date(date);
      } else {
        // Si es solo fecha (YYYY-MM-DD), parsear como local
        dateObj = parseLocalDateString(date);
      }
    } else {
      dateObj = date;
    }

    // Verificar si es una fecha válida
    if (isNaN(dateObj.getTime())) {
      return String(date);
    }

    // Formato: dd/mm/yyyy
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return String(date);
  }
}

/**
 * Formatea una fecha ISO con hora para mostrar en la UI (ej: fecha_creacion)
 * Maneja strings ISO y Date objects
 * Formato: dd/mm/yyyy
 */
export function formatDateTimeDisplay(date: Date | string | undefined | null): string {
  if (!date) return '';

  try {
    let dateObj: Date;

    if (typeof date === 'string') {
      // Parsear string ISO con o sin Z
      dateObj = new Date(date);
    } else {
      dateObj = date;
    }

    // Verificar si es una fecha válida
    if (isNaN(dateObj.getTime())) {
      return String(date);
    }

    // Formato: dd/mm/yyyy
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return String(date);
  }
}

/**
 * Calcula el número de unidades (días o noches) entre dos fechas
 * según la unidad de reserva del espacio.
 *
 * @param fechaEntrada - Fecha de entrada (YYYY-MM-DD string)
 * @param fechaSalida - Fecha de salida (YYYY-MM-DD string)
 * @param unidadReserva - 'dia' o 'noche' (si es undefined, asume 'noche')
 * @returns Número de unidades
 *
 * Ejemplos:
 * - unidad_reserva='noche': entrada 2024-01-01, salida 2024-01-03 → 2 noches
 * - unidad_reserva='dia': entrada 2024-01-01, salida 2024-01-03 → 3 días
 */
export function calcularUnidades(
  fechaEntrada: string,
  fechaSalida: string,
  unidadReserva?: string
): number {
  const start = parseLocalDateString(fechaEntrada)
  const end = parseLocalDateString(fechaSalida)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  // Si unidad_reserva es 'dia', incluimos el día de checkout
  // Si es 'noche' (o undefined por defecto), excluimos el día de checkout
  if (unidadReserva === 'dia') {
    return diffDays + 1
  } else {
    return diffDays
  }
}